import { FC, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { CONTACT_LIMIT_EVENT, ContactLimitInfo, OVER_LIMIT_EVENT } from "../../../utils/contactLimit";
import { useTranslation } from "../../../hooks/useTranslation";

/**
 * App-wide upgrade notice, mounted once near the root (see App/index.tsx,
 * beside TrialWidget). Fired by the main api.ts response interceptor (and its
 * saga-transport mirror, createResponseDispatcher.js) on two distinct
 * refusals:
 * - CONTACT_LIMIT_EVENT: 422 CONTACT_LIMIT_REACHED — this particular write
 *   would push the account over its plan's contact limit.
 * - OVER_LIMIT_EVENT: 403 OVER_CONTACT_LIMIT — the account already holds
 *   more contacts than its plan covers and is refusing writes generally
 *   (Task 17's view-only mode). Tracked in separate state (`refused`, not
 *   reused `info`) so the two dialogs don't clobber each other if both ever
 *   fire in the same session.
 *
 * Registration shows its own inline message instead (ContactIntakePanels /
 * ImportMappingModal's onLimitReached) — the interceptor deliberately never
 * dispatches either event while inside the onboarding flow, so this dialog
 * never pops up over it. See api.ts's ONBOARDING_ROOT guard.
 */
export const ContactLimitDialog: FC = () => {
  const { p } = useTranslation("contacts");
  const [info, setInfo] = useState<ContactLimitInfo | null>(null);
  const [refused, setRefused] = useState<{ current: number; limit: number } | null>(null);

  useEffect(() => {
    const onLimit = (e: Event) => setInfo((e as CustomEvent<ContactLimitInfo>).detail);
    const onOverLimit = (e: Event) => setRefused((e as CustomEvent<{ current: number; limit: number }>).detail);
    window.addEventListener(CONTACT_LIMIT_EVENT, onLimit);
    window.addEventListener(OVER_LIMIT_EVENT, onOverLimit);
    return () => {
      window.removeEventListener(CONTACT_LIMIT_EVENT, onLimit);
      window.removeEventListener(OVER_LIMIT_EVENT, onOverLimit);
    };
  }, []);

  return (
    <>
      <Dialog open={!!info} onClose={() => setInfo(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{info ? p("limit_title", { limit: info.limit }) : ""}</DialogTitle>
        <DialogContent><Typography variant="body2">{p("limit_body")}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setInfo(null)}>{p("limit_not_now")}</Button>
          <Button variant="contained" onClick={() => window.location.assign("/pricing")}>{p("limit_see_plans")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!refused} onClose={() => setRefused(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{refused ? p("over_limit_title", refused) : ""}</DialogTitle>
        <DialogContent><Typography variant="body2">{p("over_limit_refused")}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setRefused(null)}>{p("limit_not_now")}</Button>
          {/* /households, not /contacts — the real route (see OverLimitBanner's
              comment; /contacts 404s, verified in browser). */}
          <Button onClick={() => window.location.assign("/households")}>{p("over_limit_delete")}</Button>
          <Button variant="contained" onClick={() => window.location.assign("/pricing")}>{p("over_limit_upgrade")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
